import { Link, useLocation, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { auth, db } from '../firebase';
import { useI18n, useStaffTenantLocale } from '../i18n';
import { moduleOn, useTenantFeatures } from '../tenantFeatures';
import '../styles/business-dashboard.css';

const LINKS = [
    { to: '/dashboard', labelKey: 'nav.dashboard', icon: '📊' },
    { to: '/new-policy', labelKey: 'nav.newPolicy', icon: '➕', className: 'bd-btn-success' },
    { to: '/customers', labelKey: 'nav.customers', icon: '👤', className: 'bd-btn-primary' },
    { to: '/policies', labelKey: 'nav.policies', icon: '📋', className: 'bd-btn-info' },
    { to: '/renewals', labelKey: 'nav.renewals', icon: '🔁' },
    { to: '/claims', labelKey: 'nav.claims', icon: '⚠️', className: 'bd-btn-warning', module: 'claimsFnol' },
    { to: '/manager', labelKey: 'nav.approvals', icon: '✅' },
    { to: '/returns', labelKey: 'nav.graWmf', icon: '📑', roles: ['admin', 'finance', 'audit', 'manager'], module: 'graReturns' },
    { to: '/ussd', labelKey: 'nav.ussd', icon: '☎️', module: 'ussd' },
    { to: '/import', labelKey: 'nav.import', icon: '📥' },
    { to: '/team', labelKey: 'nav.team', icon: '👥', adminOnly: true },
    { to: '/settings', labelKey: 'nav.settings', icon: '🖼️', adminOnly: true }
];

function CompanyLayout({ session, children, logoUrl: logoOverride, hidePageTitle }) {
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useI18n();
    useStaffTenantLocale(session && session.tenantId);
    const features = useTenantFeatures(session && session.tenantId);
    const [logoUrl, setLogoUrl] = useState(logoOverride || '');
    const links = LINKS.filter((item) => {
        if (item.adminOnly && session.role !== 'admin') return false;
        if (item.roles && !item.roles.includes(session.role)) return false;
        if (item.module && !moduleOn(features, item.module)) return false;
        return true;
    });

    useEffect(() => {
        if (logoOverride) {
            setLogoUrl(logoOverride);
            return;
        }
        if (!session?.tenantId) return;
        getDoc(doc(db, 'tenants', session.tenantId)).then((snap) => {
            if (snap.exists()) setLogoUrl(snap.data().logoUrl || '');
        }).catch(() => {});
    }, [session, logoOverride]);

    const handleLogout = async () => {
        await signOut(auth);
        navigate('/login');
    };

    const isDashboard = location.pathname === '/dashboard';
    const pageTitle = t(links.find((item) => item.to === location.pathname)?.labelKey || 'layout.workspace');

    return (
        <div className="bd-app">
            <div className="bd-container">
                <div className="bd-header">
                    <div>
                        <div className="d-flex align-items-center gap-3 flex-wrap">
                            {logoUrl ? (
                                <img src={logoUrl} alt="" style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 12, background: '#fff', padding: 4 }} />
                            ) : null}
                            <div>
                                <h1>🏢 {t('layout.businessDashboard')}</h1>
                                <span className="bd-tagline">{t('layout.tagline')}</span>
                                <div className="bd-business-name">
                                    {session.companyName} · {session.displayName || session.email} · {String(session.role || 'staff').toUpperCase()}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="bd-header-actions">
                        <span className="bd-cloud">{t('layout.connected')}</span>
                        <Link to="/new-policy" className="bd-btn bd-btn-success bd-btn-sm">➕ {t('nav.newPolicy')}</Link>
                        <button type="button" className="bd-btn bd-btn-secondary bd-btn-sm" onClick={handleLogout}>🚪 {t('layout.logout')}</button>
                    </div>
                </div>

                <div className="bd-quick-actions">
                    <span className="bd-label">⚡ {t('layout.quickActions')}</span>
                    {links.map((item) => (
                        <Link
                            key={item.to}
                            to={item.to}
                            className={'bd-btn bd-btn-sm ' + (location.pathname === item.to ? 'active-nav' : (item.className || 'bd-btn-secondary'))}
                        >
                            {item.icon} {t(item.labelKey)}
                        </Link>
                    ))}
                </div>

                {!hidePageTitle && !isDashboard && (
                    <h2 className="bd-page-title">{pageTitle}</h2>
                )}

                {children}
            </div>
        </div>
    );
}

export default CompanyLayout;
